import type { MatchLifecycleEvent, PlayerId } from '@signalclash/shared';

// V1.5+ Slice 3 — defender-side critical-flip alert banner.
//
// One DOM element per concurrent banner; stacked vertically below the
// timer. Stack policy: at most BANNER_STACK_LIMIT visible at once;
// new banners push on top, oldest auto-expires after the appropriate
// duration. Banners with an explicit `closesAtMs` (Cut Link) show a
// live wall-clock countdown and auto-dismiss when the timer hits 0;
// the others display for BANNER_DURATION_MS and then fade.
//
// Pure presentation (Rule 2). Reads only the lifecycle event; never
// touches sim state. The handler key — `victimPlayerId === viewerId`
// — is enforced in `handleEvent` so callers don't have to filter.
//
// Pattern mirrors `ReconnectBanner` (rAF-driven countdown,
// mount/unmount via inject + ensureRoot pair). Kept separate so the
// V2 reconnect banner stays single-purpose.

const ROOT_ID = 'critical-flip-banners';
const STYLE_ID = 'critical-flip-banner-styles';
const BANNER_STACK_LIMIT = 3;
const BANNER_DURATION_MS = 4000;
// V1.5+ Slice 8 — Signal Storm Endgame. The storm runs for 60 seconds;
// the banner sticks for the full window, ticking down each rAF frame.
const SIGNAL_STORM_BANNER_DURATION_MS = 60_000;
// V1.5+ Signal Surge — the surge runs for 20 seconds; the banner sticks
// for the full window with a live countdown.
const SIGNAL_SURGE_BANNER_DURATION_MS = 20_000;

type CriticalFlipReason =
  | 'link_cut'
  | 'relay_isolated'
  | 'mini_turret_dark'
  | 'reconnected'
  | 'relay_jammed'
  | 'uplink_disrupted'
  | 'relay_fortified';

// V1.5+ Slice 8 — banner reason union including the lifecycle-event
// variant (storm). Internal type — the public lifecycle event distinguishes
// `signal_storm_started` from `critical_flip` at the union level so audio /
// other consumers don't have to read a "reason" string. The banner is the
// only consumer that needs both in one rendering pipeline.
type BannerReason = CriticalFlipReason | 'signal_storm' | 'signal_surge';

interface BannerStyleSpec {
  label: string;
  // CSS class suffix → severity colour. Driven entirely by the
  // stylesheet; no inline style.
  variant: 'danger' | 'warning' | 'recovery' | 'caution' | 'success' | 'storm';
  // True ⇒ the banner shows a wall-clock countdown derived from
  // `closesAtMs`. Cut Link is the only Slice 3 reason with a fixed
  // window, so it's the only one that ticks.
  countdown: boolean;
}

const REASON_SPECS: Record<BannerReason, BannerStyleSpec> = {
  link_cut: { label: 'SIGNAL LINK CUT', variant: 'danger', countdown: true },
  relay_isolated: { label: 'RELAY ISOLATED', variant: 'danger', countdown: false },
  mini_turret_dark: { label: 'FORWARD TURRET OFFLINE', variant: 'warning', countdown: false },
  reconnected: { label: 'RECONNECTED', variant: 'recovery', countdown: false },
  // V1.5+ Slice 5 — Jam Relay banner. Orange (warning) rather than red
  // because the chain isn't broken — only vision drops at the jammed
  // node. Countdown true: the jam window has a fixed wall-clock deadline
  // (same shape as link_cut's countdown).
  relay_jammed: { label: 'RELAY JAMMED', variant: 'warning', countdown: true },
  // V1.5+ Slice 6 — Disrupt Uplink banner. Yellow (caution) — distinct
  // from cut (red danger) and jam (orange warning) per Rule 21's
  // one-second-readability requirement. The disruption is mid-tier:
  // forward firepower is degraded but not killed, so the colour sits
  // below cut/isolated but above the recovery pulse.
  uplink_disrupted: { label: 'UPLINK DISRUPTED', variant: 'caution', countdown: true },
  // V1.5+ Slice 7 — Fortify Relay banner. Green (success) — the only
  // "good news" variant for the FORTIFIER (the lifecycle event's
  // victimPlayerId carries the fortifier's id, see CriticalFlipEmitter).
  // Distinct cyan-green from `recovery` (also green-ish) so the player
  // can tell "I just successfully fortified" from "my chain reconnected"
  // at a glance per Rule 21. Countdown true — same fixed-window shape as
  // jam / disrupt.
  relay_fortified: { label: 'RELAY FORTIFIED', variant: 'success', countdown: true },
  // V1.5+ Slice 8 — Signal Storm Endgame. Purple `'storm'` variant —
  // distinct from cut (red), jam (orange), disrupt (yellow), reconnect
  // (green), fortify (cyan-green) at one second of viewing per Rule 21.
  // Sits across the colour wheel from every existing tactical banner so
  // a player reading the screen for a single frame can tell "global
  // rule change" from "tactical disruption". The "60s" suffix is filled
  // by formatLabel from the wall-clock countdown — same shape as the
  // other countdown-true banners.
  signal_storm: { label: 'SIGNAL STORM', variant: 'storm', countdown: true },
  // V1.5+ Signal Surge — short 20 s mid-match capture-speed window.
  // Reuses the existing 'recovery' variant so it reads as a "good time
  // to push" cue (capture is twice as fast) rather than danger. Distinct
  // from reconnect's recovery use because surge has a live countdown.
  signal_surge: { label: 'SIGNAL SURGE — 2× CAPTURE', variant: 'recovery', countdown: true },
};

interface ActiveBanner {
  el: HTMLDivElement;
  labelEl: HTMLSpanElement;
  closesAtMs: number | null;
  expiryTimer: ReturnType<typeof setTimeout> | null;
  spec: BannerStyleSpec;
}

export class CriticalFlipBanner {
  private root: HTMLDivElement | null = null;
  private viewerId: PlayerId;
  private readonly active: ActiveBanner[] = [];
  private rafHandle: number | null = null;

  constructor(viewerId: PlayerId) {
    this.viewerId = viewerId;
    injectStyles();
    this.root = ensureRoot();
  }

  // V2.5+ network mode: ClientApp updates the viewer slot when the
  // server's `assigned` message lands. Banner doesn't have to re-render
  // anything — already-displayed events keep showing (they were valid
  // at emit time); future events will be filtered against the new
  // viewer id.
  setViewerId(viewerId: PlayerId): void {
    this.viewerId = viewerId;
  }

  // Handle a lifecycle event. Filters by `victimPlayerId === viewerId`
  // so callers can pass every event through without pre-filtering. The
  // attacker's perspective is the world-space pulse + Sfx — no banner
  // stack noise on their side.
  //
  // V1.5+ Slice 8 — the storm-started event is a global, viewer-agnostic
  // signal (every player sees the same banner), so it bypasses the
  // viewer filter and uses the canonical 60-second wall-clock window.
  handleEvent(event: MatchLifecycleEvent): void {
    if (event.kind === 'signal_storm_started') {
      this.pushBanner('signal_storm', Date.now() + SIGNAL_STORM_BANNER_DURATION_MS);
      return;
    }
    if (event.kind === 'signal_surge_started') {
      this.pushBanner('signal_surge', Date.now() + SIGNAL_SURGE_BANNER_DURATION_MS);
      return;
    }
    if (event.kind !== 'critical_flip') return;
    if (event.victimPlayerId !== this.viewerId) return;
    this.pushBanner(event.reason as CriticalFlipReason, event.closesAtMs);
  }

  // Test seam — drives the banner from a hand-built event.
  pushForTest(reason: BannerReason, closesAtMs: number | null): void {
    this.pushBanner(reason, closesAtMs);
  }

  // Visible-banner count — exposed for tests so they can assert
  // "filtered out an enemy event" without reaching into the DOM.
  visibleCount(): number {
    return this.active.length;
  }

  hide(): void {
    if (this.root !== null) this.root.style.display = 'none';
  }

  show(): void {
    if (this.root !== null) this.root.style.display = 'flex';
  }

  // Drop everything. Used on scene transitions / match end so a
  // mid-match cut doesn't leak into the EOM modal.
  clear(): void {
    for (const b of this.active) {
      if (b.expiryTimer !== null) clearTimeout(b.expiryTimer);
      b.el.remove();
    }
    this.active.length = 0;
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
  }

  dispose(): void {
    this.clear();
    if (this.root !== null) this.root.remove();
    this.root = null;
  }

  private pushBanner(reason: BannerReason, closesAtMs: number | null): void {
    if (this.root === null) return;
    const spec = REASON_SPECS[reason];
    const el = document.createElement('div');
    el.className = `critical-flip-banner critical-flip-banner--${spec.variant}`;
    const labelEl = document.createElement('span');
    labelEl.className = 'critical-flip-banner__label';
    labelEl.textContent = formatLabel(spec, closesAtMs);
    el.appendChild(labelEl);
    this.root.prepend(el);
    const banner: ActiveBanner = {
      el,
      labelEl,
      closesAtMs,
      expiryTimer: null,
      spec,
    };
    this.active.unshift(banner);

    // Trim to the visible cap. Drop oldest (now at the bottom of the
    // stack) so the most recent flips stay readable.
    while (this.active.length > BANNER_STACK_LIMIT) {
      const dropped = this.active.pop();
      if (dropped !== undefined) {
        if (dropped.expiryTimer !== null) clearTimeout(dropped.expiryTimer);
        dropped.el.remove();
      }
    }

    // Schedule auto-expiry. Countdown banners (link_cut) auto-dismiss
    // when their wall-clock window closes; the others use the fixed
    // BANNER_DURATION_MS.
    const dismissAt = closesAtMs !== null && spec.countdown
      ? closesAtMs
      : Date.now() + BANNER_DURATION_MS;
    banner.expiryTimer = setTimeout(() => this.dismiss(banner), Math.max(0, dismissAt - Date.now()));

    // Make sure the rAF countdown loop is running if any visible
    // banner has a live countdown.
    if (spec.countdown) this.ensureRaf();

    this.show();
  }

  private dismiss(banner: ActiveBanner): void {
    if (banner.expiryTimer !== null) {
      clearTimeout(banner.expiryTimer);
      banner.expiryTimer = null;
    }
    const idx = this.active.indexOf(banner);
    if (idx === -1) return;
    this.active.splice(idx, 1);
    banner.el.remove();
    if (this.active.length === 0 && this.root !== null) {
      this.hide();
    }
  }

  private ensureRaf(): void {
    if (this.rafHandle !== null) return;
    this.rafHandle = requestAnimationFrame(this.rafTick);
  }

  private readonly rafTick = (): void => {
    let anyActiveCountdown = false;
    for (const banner of this.active) {
      if (!banner.spec.countdown) continue;
      anyActiveCountdown = true;
      banner.labelEl.textContent = formatLabel(banner.spec, banner.closesAtMs);
    }
    if (anyActiveCountdown) {
      this.rafHandle = requestAnimationFrame(this.rafTick);
    } else {
      this.rafHandle = null;
    }
  };
}

function formatLabel(spec: BannerStyleSpec, closesAtMs: number | null): string {
  if (!spec.countdown || closesAtMs === null) return spec.label;
  const remainingMs = Math.max(0, closesAtMs - Date.now());
  const remainingSec = Math.ceil(remainingMs / 1000);
  return `${spec.label} — ${remainingSec}s`;
}

function ensureRoot(): HTMLDivElement {
  const existing = document.getElementById(ROOT_ID);
  if (existing instanceof HTMLDivElement) return existing;
  const div = document.createElement('div');
  div.id = ROOT_ID;
  document.body.appendChild(div);
  return div;
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
#${ROOT_ID} {
  position: absolute;
  top: 60px;
  left: 50%;
  transform: translateX(-50%);
  display: none;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  z-index: 26;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  pointer-events: none;
}
.critical-flip-banner {
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 12px;
  letter-spacing: 0.06em;
  border: 1px solid currentColor;
  background: rgba(20, 16, 16, 0.92);
  box-shadow: 0 0 14px currentColor;
  animation: critical-flip-banner-pop 220ms ease-out;
}
.critical-flip-banner--danger { color: #e2543c; }
.critical-flip-banner--warning { color: #f5b76e; }
.critical-flip-banner--recovery { color: #4ad06b; }
.critical-flip-banner--caution { color: #e8d96b; }
.critical-flip-banner--success { color: #5fe5c7; }
.critical-flip-banner--storm { color: #b889e8; }
.critical-flip-banner__label { color: inherit; }
@keyframes critical-flip-banner-pop {
  0%   { transform: translateY(-6px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
}
/* V3 mobile: on mobile the HUD timer is at top:4px and the overtime /
 * dominance banner sits at top:38px. Pushing the critical-flip stack to
 * top:72px keeps it below both without manual z-index wrestling. Font
 * stays the same — these alerts need to be read in one second. */
@media (max-width: 820px), (pointer: coarse) {
  #${ROOT_ID} {
    top: 72px;
  }
}
`;
  document.head.appendChild(style);
}
